import { SIDEBAR_WIDTH } from '@/theme';
import { Box, styled } from '@mui/material';
import { KeyboardArrowRight } from '@mui/icons-material';
import React, { memo, useCallback, useState } from 'react';

const NORMAL_WIDTH = SIDEBAR_WIDTH;
const WIDE_WIDTH = SIDEBAR_WIDTH * 2;

const StyledSidePanel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: 0,
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  zIndex: theme.zIndex.drawer,
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  transition: 'width 0.3s ease-in-out',
}));

const StyledToggleHandle = styled(Box, {
  shouldForwardProp: property => property !== 'isWide',
})<{ isWide: boolean }>(({ theme, isWide }) => ({
  position: 'absolute',
  top: 0,
  right: -10,
  width: 10,
  height: '100%',
  cursor: isWide ? 'w-resize' : 'e-resize',
  backgroundColor: theme.palette.background.paper,
  transition: 'background-color 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, .5)',
  },
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: theme.palette.divider,
    transition: 'background-color 0.2s ease',
  },
  '&::before': {
    left: 0,
  },
  '&::after': {
    right: 0,
  },
}));

const ActionIcon = styled(KeyboardArrowRight, {
  shouldForwardProp: property => property !== 'isWide',
})<{ isWide: boolean }>(({ isWide }) => ({
  fontSize: 14,
  color: 'rgba(25, 118, 210, .5)',
  transform: isWide ? 'scaleX(-1)' : 'none',
}));

const ToggleHandle = memo(({ isWide, onClick }: { isWide: boolean; onClick: () => void }) => {
  return (
    <StyledToggleHandle isWide={isWide} onClick={onClick} data-testid='side-panel-toggle-handle'>
      <ActionIcon isWide={isWide} />
    </StyledToggleHandle>
  );
});

ToggleHandle.displayName = 'ToggleHandle';

export const LeftPanel = ({
  children,
  className,
  style,
  onWidthChange,
  ...properties
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onWidthChange?: (width: number) => void;
} & React.ComponentProps<typeof Box>) => {
  const [isWide, setIsWide] = useState(false);

  const width = isWide ? WIDE_WIDTH : NORMAL_WIDTH;

  const handleToggle = useCallback(() => {
    setIsWide(!isWide);
    onWidthChange?.(width);
  }, [isWide, onWidthChange, width]);

  return (
    <StyledSidePanel
      className={className}
      style={{
        width,
        ...style,
      }}
      {...properties}
    >
      {children}
      <ToggleHandle isWide={isWide} onClick={handleToggle} />
    </StyledSidePanel>
  );
};
